// The idle sea view of the map (R2865, ui-map-interface.md §13 "SEA VIEW").
//
// After the idle time of the SEA VIEW section with no input, the map blends
// into the sea view, and the sea view plays its ACTION tour. The sea view
// runs in one frame (an iframe of `/seaview/?…&embed=1`) over the live map:
// the map keeps its socket and its state under the frame, so the return is
// instant.
//
// The phases, in order:
//
//   idle   the idle clock runs. Each input on the map resets it, and a mouse
//          move counts, because the player who moves the mouse is present.
//   count  `warn_s` before the start (the span of the band that holds the
//          idle time, R2893): the readout line shows the SEA VIEW
//          timer, and the frame loads hidden (opacity 0, no pointer), so the
//          start has no loading time. Any input cancels the countdown and
//          removes the frame.
//   play   the countdown is over and the frame is `ready`: the map sent
//          `play`, and the timer holds at 0s until the frame answers
//          `playing`. A frame that is not ready at 0s holds the timer
//          there too, and the map sends `play` at `ready`.
//   rise   the frame fades in over `fade_in_ms`; a shield over it takes the
//          pointer. At the start of the fade the map goes quiet (no
//          effect, no spoken line; its music plays on, R2977). At the end
//          of the fade the map pauses its frame loop.
//   tour   the tour plays. SPACE (`[keys.seaidle] free`) sends `free`;
//          every other key, a pointer press, a touch and a wheel go back to
//          the map. A mouse move does nothing: a screensaver must not end
//          on a small move of the desk. The map swallows the input that
//          ends the tour, so no map hotkey fires on it.
//   free   the free sea view takes the input. Its BACK TO MAP sends `back`
//          with the eye place, and the map centres its camera there.
//   fall   the map runs its frame loop again, its effects sound again, and
//          the frame fades out over `fade_out_ms`; then the map sends
//          `hold` and removes it.
//
// The message envelope is `{ type: 'ap.seaview', act, x?, y? }` (the embed
// mode of the sea view, seaview.md). A message counts only when its source
// is the frame's window and its origin the origin of the frame address.
//
// The controller `SeaIdle` reads no DOM: a host hands in the clock, the
// gate, the frame and the map hooks, so a test drives it with fakes.
// `bindSeaIdle` and `mountSeaFrame` are the DOM half.

import { KEYS, SEAVIEW_IDLE } from '../gen/presentation';
import { options as S } from '../gen/strings';
import { matches, type KeyPress } from '../keys';

/** The `type` of every message between the map and the sea view. */
export const SEA_MSG = 'ap.seaview';

/** The acts the map sends to the frame. */
export type SeaOut = 'play' | 'free' | 'hold';

/** One message of the frame, as the map reads it. */
export interface SeaIn {
  act: string;
  x?: number;
  y?: number;
}

/** The message in `data`, or null when it is not one of the envelope. */
export function readSeaMsg(data: unknown): SeaIn | null {
  if (typeof data !== 'object' || data === null) return null;
  const m = data as Record<string, unknown>;
  if (m.type !== SEA_MSG || typeof m.act !== 'string') return null;
  const out: SeaIn = { act: m.act };
  if (typeof m.x === 'number' && Number.isFinite(m.x)) out.x = m.x;
  if (typeof m.y === 'number' && Number.isFinite(m.y)) out.y = m.y;
  return out;
}

/** What the clock needs to run (§13 "SEA VIEW"). */
export interface SeaIdleGate {
  /** The switch IDLE SEA VIEW. */
  setting: boolean;
  /** `navigator.gpu`: the sea view draws with WebGPU alone. */
  gpu: boolean;
  /** `prefers-reduced-motion`: the tour moves the eye on its own
   *  (seaview.md §6.9.1). */
  reducedMotion: boolean;
  /** The page stands in a world and holds its hello. */
  world: boolean;
  /** A text field has the focus. */
  typing: boolean;
}

/** True when the idle clock may run. */
export function seaIdleAllowed(g: SeaIdleGate): boolean {
  return g.setting && g.gpu && !g.reducedMotion && g.world && !g.typing;
}

/** True when the focus stands in a field the player types into. A range,
 *  a switch or a button with the focus is no text field. */
export function isTyping(doc: Document): boolean {
  const el = doc.activeElement as HTMLElement | null;
  if (!el) return false;
  if (el.isContentEditable || el.tagName === 'TEXTAREA') return true;
  if (el.tagName !== 'INPUT') return false;
  return !['range', 'checkbox', 'radio', 'button', 'submit', 'reset'].includes((el as HTMLInputElement).type);
}

/** The frame as the controller drives it. `mountSeaFrame` is the DOM one. */
export interface SeaFrame {
  /** The frame's window: a message from any other source is refused. */
  readonly source: unknown;
  /** The origin of the frame address: the target of every message the map
   *  sends, and the one origin a message of the frame may carry. */
  readonly origin: string;
  post(act: SeaOut): void;
  /** Fades the frame in or out over `ms`, then calls `done`. */
  fade(show: boolean, ms: number, done: () => void): void;
  /** On, the shield over the frame takes every pointer, and the frame takes
   *  no input and no focus. Off, the frame takes the input and the focus. */
  shield(on: boolean): void;
  remove(): void;
}

/** What the controller reads and calls. */
export interface SeaIdleDeps {
  /** The wall clock, in ms. */
  now(): number;
  /** The gate (`seaIdleAllowed`). */
  allowed(): boolean;
  /** The document is hidden: the clock holds. */
  hidden(): boolean;
  /** The idle time of the setting, in minutes. */
  idleMin(): number;
  /** The frame address, or null when the page cannot name one. */
  href(): string | null;
  mount(href: string): SeaFrame;
  /** Pauses the map's frame loop (on) or runs it again (off). */
  pauseMap(on: boolean): void;
  /** Hushes the map's effects and spoken lines (on) or lets them sound
   *  again (off). The map's music plays on (R2977). */
  hushMap(on: boolean): void;
  /** Centres the map camera on a place in world px. */
  centre(x: number, y: number): void;
  /** The countdown came or went: the readout line draws again. */
  changed(): void;
}

/** The phases of the idle sea view, in order. */
export type SeaIdlePhase = 'idle' | 'count' | 'play' | 'rise' | 'tour' | 'free' | 'fall';

/** One input of the map window. */
export type SeaInput = 'key' | 'keyup' | 'down' | 'move' | 'wheel' | 'touch';

/** The countdown span in seconds for an idle time of `min` minutes: the
 *  span of the last band whose least idle time `min` reaches (R2893). */
export function seaIdleWarnS(min: number): number {
  let s: number = SEAVIEW_IDLE.warn[0].s;
  for (const b of SEAVIEW_IDLE.warn) if (min >= b.fromMin) s = b.s;
  return s;
}

/** The idle clock, the countdown and the frame of the idle sea view. */
export class SeaIdle {
  private ph: SeaIdlePhase = 'idle';
  /** The wall ms of the last input. */
  private last: number;
  /** The wall ms of the start, and the span of the countdown. */
  private end = 0;
  private span = 0;
  /** The frame answered `ready`. */
  private ready = false;
  private frame: SeaFrame | null = null;
  private paused = false;
  private hushed = false;
  /** The code of the key that ended the tour or took the free view: its
   *  keyup is swallowed too. */
  private endCode = '';

  constructor(private readonly d: SeaIdleDeps) {
    this.last = d.now();
  }

  get phase(): SeaIdlePhase { return this.ph; }

  /** The countdown in wall seconds, or null while none runs. */
  countdown(): { leftSec: number; totalSec: number } | null {
    if (this.ph !== 'count' && this.ph !== 'play') return null;
    return { leftSec: Math.max(0, (this.end - this.d.now()) / 1000), totalSec: this.span / 1000 };
  }

  /** The delay to the next tick, in ms: a quarter second while the
   *  countdown runs, else one second. */
  nextDelay(): number {
    return this.ph === 'count' || this.ph === 'play' ? 250 : 1000;
  }

  /** One step of the idle clock. */
  tick(): void {
    if (this.ph !== 'idle' && this.ph !== 'count' && this.ph !== 'play') return;
    const now = this.d.now();
    // A hidden tab holds the clock, and so does a closed gate: the clock
    // starts from zero when the tab shows again.
    if (!this.d.allowed() || this.d.hidden()) {
      if (this.ph !== 'idle') this.drop();
      this.last = now;
      return;
    }
    if (this.ph === 'idle') {
      const min = this.d.idleMin();
      const idleMs = (min > 0 ? min : SEAVIEW_IDLE.minMin) * 60_000;
      const warnMs = Math.min(idleMs, seaIdleWarnS(idleMs / 60_000) * 1000);
      if (now - this.last < idleMs - warnMs) return;
      const href = this.d.href();
      if (href === null) return;
      this.frame = this.d.mount(href);
      this.ready = false;
      this.end = this.last + idleMs;
      this.span = warnMs;
      this.ph = 'count';
      this.d.changed();
    }
    if (this.ph === 'count' && this.ready && now >= this.end) this.play();
  }

  /**
   * One input of the map window. Answers true when the host must swallow
   * it (`preventDefault` and `stopImmediatePropagation`).
   */
  input(kind: SeaInput, key?: KeyPress): boolean {
    if (kind === 'keyup') {
      if (key && this.endCode !== '' && key.code === this.endCode) {
        this.endCode = '';
        return true;
      }
      return false;
    }
    switch (this.ph) {
      case 'idle':
      case 'count':
      case 'play':
        this.last = this.d.now();
        if (this.ph !== 'idle') this.drop();
        return false;
      case 'rise':
      case 'tour':
        if (kind === 'move') return false;
        if (kind === 'key' && key) {
          this.endCode = key.code;
          if (matches(KEYS.seaidle.free, key)) {
            this.free();
            return true;
          }
        }
        this.back(null);
        return true;
      case 'free':
        return false;
      case 'fall':
        // The shield stands until the frame goes, so a press lands on it.
        return kind !== 'key' && kind !== 'move';
    }
  }

  /** One message of the window. */
  message(ev: { source: unknown; origin: string; data: unknown }): void {
    const f = this.frame;
    if (!f || ev.source !== f.source || ev.origin !== f.origin) return;
    const m = readSeaMsg(ev.data);
    if (!m) return;
    switch (m.act) {
      case 'ready':
        if (this.ph !== 'count') return;
        this.ready = true;
        if (this.d.now() >= this.end) this.play();
        return;
      case 'playing':
        if (this.ph === 'play') this.rise(f);
        return;
      case 'back':
        if (this.ph === 'rise' || this.ph === 'tour' || this.ph === 'free') {
          this.back(m.x !== undefined && m.y !== undefined ? { x: m.x, y: m.y } : null);
        }
        return;
    }
  }

  /** Takes the frame down at once: the page leaves the world. */
  dispose(): void {
    if (this.frame) this.drop();
  }

  private play(): void {
    this.frame?.post('play');
    this.ph = 'play';
  }

  private rise(f: SeaFrame): void {
    this.ph = 'rise';
    f.shield(true);
    this.hush(true);
    this.d.changed();
    f.fade(true, SEAVIEW_IDLE.fadeInMs, () => {
      if (this.frame !== f) return;
      if (this.ph === 'rise') this.ph = 'tour';
      // The frame covers the map now: the map draws nothing under it.
      if (this.ph === 'tour' || this.ph === 'free') this.pause(true);
    });
  }

  private free(): void {
    const f = this.frame;
    if (!f) return;
    f.post('free');
    this.ph = 'free';
    f.shield(false);
  }

  /** Back to the map: the map draws again before the frame fades out. */
  private back(at: { x: number; y: number } | null): void {
    const f = this.frame;
    if (!f) return;
    this.ph = 'fall';
    f.shield(true);
    this.pause(false);
    this.hush(false);
    if (at) this.d.centre(at.x, at.y);
    f.fade(false, SEAVIEW_IDLE.fadeOutMs, () => {
      if (this.frame === f) this.drop();
    });
  }

  /** Sends `hold`, removes the frame and starts the idle clock again. */
  private drop(): void {
    const f = this.frame;
    this.frame = null;
    this.pause(false);
    this.hush(false);
    f?.post('hold');
    f?.remove();
    this.ph = 'idle';
    this.ready = false;
    this.last = this.d.now();
    this.d.changed();
  }

  private pause(on: boolean): void {
    if (this.paused === on) return;
    this.paused = on;
    this.d.pauseMap(on);
  }

  private hush(on: boolean): void {
    if (this.hushed === on) return;
    this.hushed = on;
    this.d.hushMap(on);
  }
}

/**
 * The arrival that a hello starts while the idle sea view covers the map
 * (R2882, ui-map-interface.md §13 "SEA VIEW"). A reconnect or a season
 * roll under the frame sends the map a hello, and the map starts its
 * arrival. The arrival holds until the frame loop is calm (R1533), and the
 * loop is paused under the frame, so the arrival waited for the return and
 * played its wave and its cue there. The return shows the map as it
 * stands: the host skips that arrival when the map runs again, and its cue
 * stays silent. An arrival that starts on the map in view plays as before.
 */
export class CoveredArrival {
  private covered = false;
  private held = false;

  /** A hello started an arrival: it is the covered one while the map is paused. */
  started(): void {
    this.held = this.covered;
  }

  /** True while the cue of the arrival must stay silent: the arrival is the covered one. */
  get quiet(): boolean {
    return this.held;
  }

  /**
   * The map paused (on) or runs again (off). At the return a covered
   * arrival is skipped through `skip`, which reads `quiet` true for its cue.
   */
  cover(on: boolean, skip: () => void): void {
    this.covered = on;
    if (on || !this.held) return;
    skip();
    this.held = false;
  }
}

/**
 * Puts the controller on the window: the inputs in the capture phase, the
 * messages, the visibility and the tick. Answers the unbind.
 */
export function bindSeaIdle(c: SeaIdle, win: Window = window, doc: Document = document): () => void {
  const swallow = (ev: Event, cancel: boolean): void => {
    if (cancel && ev.cancelable) ev.preventDefault();
    ev.stopImmediatePropagation();
  };
  const onKey = (kind: 'key' | 'keyup') => (ev: KeyboardEvent): void => {
    if (c.input(kind, ev)) swallow(ev, true);
  };
  // The wheel and the touch are passive, so the page scrolls as before; a
  // swallowed one only stops its propagation (its target is the shield).
  const onPointer = (kind: SeaInput, cancel: boolean) => (ev: Event): void => {
    if (c.input(kind)) swallow(ev, cancel);
  };
  const bindings: [string, EventListener, AddEventListenerOptions][] = [
    ['keydown', onKey('key') as EventListener, { capture: true }],
    ['keyup', onKey('keyup') as EventListener, { capture: true }],
    ['pointerdown', onPointer('down', true), { capture: true }],
    ['pointermove', onPointer('move', false), { capture: true, passive: true }],
    ['wheel', onPointer('wheel', false), { capture: true, passive: true }],
    ['touchstart', onPointer('touch', false), { capture: true, passive: true }],
  ];
  for (const [type, fn, opts] of bindings) win.addEventListener(type, fn, opts);
  const onMessage = (ev: MessageEvent): void => c.message(ev);
  win.addEventListener('message', onMessage);
  const onVisible = (): void => c.tick();
  doc.addEventListener('visibilitychange', onVisible);
  let timer = 0;
  const loop = (): void => {
    c.tick();
    timer = win.setTimeout(loop, c.nextDelay());
  };
  timer = win.setTimeout(loop, c.nextDelay());
  return () => {
    for (const [type, fn, opts] of bindings) win.removeEventListener(type, fn, opts);
    win.removeEventListener('message', onMessage);
    doc.removeEventListener('visibilitychange', onVisible);
    win.clearTimeout(timer);
    c.dispose();
  };
}

/**
 * The DOM frame (`#seaidle` on `document.body`, `ui/styles.css`): the
 * iframe and the shield over it. The host fades on its opacity with the
 * curve `fade_ease`; the duration of each fade is set inline.
 */
export function mountSeaFrame(href: string, doc: Document = document): SeaFrame {
  const host = doc.createElement('div');
  host.id = 'seaidle';
  host.setAttribute('aria-hidden', 'true');
  host.style.transitionTimingFunction = SEAVIEW_IDLE.fadeEase;
  const frame = doc.createElement('iframe');
  frame.title = S.sec_seaview;
  frame.tabIndex = -1;
  frame.inert = true;
  frame.src = href;
  const shield = doc.createElement('div');
  shield.className = 'shield';
  // The release of the press that ends the tour lands on the shield; no
  // listener of the map may read it as a press of its own.
  for (const type of ['pointerup', 'click', 'dblclick', 'contextmenu']) {
    shield.addEventListener(type, (ev) => { ev.preventDefault(); ev.stopPropagation(); });
  }
  host.append(frame, shield);
  doc.body.appendChild(host);
  const origin = new URL(href, doc.baseURI).origin;
  let timer = 0;
  return {
    get source() { return frame.contentWindow; },
    origin,
    post(act) { frame.contentWindow?.postMessage({ type: SEA_MSG, act }, origin); },
    fade(show, ms, done) {
      clearTimeout(timer);
      host.style.transitionDuration = `${ms}ms`;
      host.style.opacity = show ? '1' : '0';
      timer = window.setTimeout(done, ms);
    },
    shield(on) {
      host.classList.toggle('shield', on);
      host.classList.toggle('free', !on);
      host.setAttribute('aria-hidden', String(on));
      frame.inert = on;
      if (!on) frame.focus();
    },
    remove() {
      clearTimeout(timer);
      host.remove();
    },
  };
}
